import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, BookOpen, Palette, Megaphone, Leaf, HeartPulse, Users, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { MapLink } from '../ui/MapLink';
import { heroContent, programs as mockPrograms, stats, testimonials, events as mockEvents } from '../../data/mock';
import api from '../../lib/api';
import { useContent } from '../../context/ContentContext';
import { KickoffBanner } from '../ui/KickoffBanner';
import WCLCLogo from '../ui/WCLCLogo';

// Hero Section - Minimal, editorial with logo
const HeroSection = () => {
  const { c } = useContent();
  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${c('home.hero.image', heroContent.backgroundImage)})` }}
      />
      {/* Overlay - Lighter for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#f1e3eb]/70 via-[#f1e3eb]/40 to-[#f1e3eb]/85" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Seal - Large, matching the height of paragraph + CTA buttons */}
          <div className="flex-shrink-0" data-testid="hero-seal-logo">
            <WCLCLogo size="custom" className="h-48 sm:h-60 lg:h-[320px]" />
          </div>

          {/* Content */}
          <div className="max-w-3xl">
            <div className="decorative-line mb-8 animate-fade-in" />
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#3c4a2a] mb-6 animate-fade-in-up drop-shadow-sm">
              {c('home.hero.title', heroContent.title)}
            </h1>
            <p className="text-lg sm:text-xl text-[#3c4a2a]/85 mb-10 leading-relaxed animate-fade-in-up delay-200">
              {c('home.hero.subtitle', heroContent.subtitle)}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
              <Link to="/about">
                <Button
                  variant="outline"
                  data-testid="hero-learn-more-btn"
                  className="w-full sm:w-auto border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white px-8 py-6 text-lg"
                >
                  {c('home.hero.button', 'Learn More')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#f1e3eb] to-transparent" />
    </section>
  );
};

// Stats Section - Clean white
const StatsSection = () => (
  <section className="py-16 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
        {stats.map((stat, index) => (
          <div
            key={stat.label}
            className="text-center animate-fade-in-up"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <p className="font-serif text-4xl sm:text-5xl text-[#5d6b3c] mb-2">
              {stat.value}
            </p>
            <p className="text-[#5f6d51] text-sm uppercase tracking-wider">
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Mission Preview Section - Cream background
const MissionSection = () => {
  const { c } = useContent();
  return (
    <section className="py-20 bg-[#f1e3eb]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[#5d6b3c] text-xs uppercase tracking-[0.2em] font-medium">
              {c('home.mission.eyebrow', 'Our Mission')}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-6">
              {c('home.mission.heading', 'Empowering Women, Advancing Community')}
            </h2>
            <p className="text-[#5f6d51] text-lg leading-relaxed mb-8">
              {c('home.mission.text', "The Women's Club of Lincoln County is a nonpartisan organization dedicated to cultivating community advancement through women's leadership while empowering every woman to grow into her truest self.")}
            </p>
            <Link to="/about">
              <Button
                variant="link"
                className="text-[#5d6b3c] hover:text-[#3c4a2a] p-0 text-base"
              >
                {c('home.mission.link', 'Learn more about our story')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="relative">
            <div className="pink-ombre aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img
                src={c('home.mission.image', 'https://images.pexels.com/photos/4063791/pexels-photo-4063791.jpeg')}
                alt="Women mentoring and supporting each other"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Subtle decorative elements */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#bd8cae]/20 rounded-full -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Programs Preview Section
const iconMap = {
  Palette: Palette,
  Megaphone: Megaphone,
  BookOpen: BookOpen,
  Leaf: Leaf,
  HeartPulse: HeartPulse,
  Heart: Heart,
  Users: Users,
};

const ProgramsSection = ({ programs }) => {
  const { c } = useContent();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#5d6b3c] text-xs uppercase tracking-[0.2em] font-medium">
            {c('home.programs.eyebrow', 'What We Do')}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-4">
            {c('home.programs.heading', 'Our Five Focus Areas')}
          </h2>
          <p className="text-[#5f6d51]">
            {c('home.programs.text', 'Our five focus areas align with the GFWC community service pillars, creating lasting impact across Lincoln County.')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {programs.slice(0, 3).map((program) => {
            const IconComponent = iconMap[program.icon] || Users;
            return (
              <Card
                key={program.id}
                className="group bg-white border border-[#5f6d51]/10 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-full bg-[#f1e3eb] flex items-center justify-center mb-4">
                    <IconComponent className="w-5 h-5 text-[#5d6b3c]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#3c4a2a] mb-3">
                    {program.title}
                  </h3>
                  <p className="text-[#5f6d51] text-sm mb-4 line-clamp-2">
                    {program.description}
                  </p>
                  <Link
                    to={`/programs#${program.id}`}
                    className="inline-flex items-center text-[#5d6b3c] hover:text-[#3c4a2a] text-sm font-medium"
                  >
                    Learn more
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Link to="/programs">
            <Button
              variant="outline"
              className="border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white px-8"
            >
              View All Programs
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Upcoming Events Section - Subtle rose tint
const EventsSection = ({ events }) => {
  const { c } = useContent();
  return (
    <section className="py-20 bg-[#bd8cae]/8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12">
          <div>
            <span className="text-[#5d6b3c] text-xs uppercase tracking-[0.2em] font-medium">
              {c('home.events.eyebrow', 'Mark Your Calendar')}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3">
              {c('home.events.heading', 'Upcoming Events')}
            </h2>
          </div>
          <Link to="/events" className="mt-4 sm:mt-0">
            <Button
              variant="link"
              className="text-[#5d6b3c] hover:text-[#3c4a2a] p-0"
            >
              View all events
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.filter((e) => e.event_type !== 'community' && new Date(`${e.date}T23:59:59`) >= new Date()).slice(0, 3).map((event) => {
            const eventDate = new Date(`${event.date}T12:00:00`);
            return (
              <Card
                key={event.id}
                className="bg-white border border-[#5f6d51]/10 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Date badge - Authority green */}
                  <div className="absolute top-4 left-4 z-10 bg-[#5d6b3c] text-white px-3 py-2 rounded-lg text-center">
                    <p className="text-2xl font-bold leading-none">
                      {eventDate.getDate()}
                    </p>
                    <p className="text-xs uppercase">
                      {eventDate.toLocaleString('default', { month: 'short' })}
                    </p>
                  </div>
                </div>
                <CardContent className="p-6">
                  {/* Category badge - Coral for small highlight */}
                  <span className="inline-block text-xs text-[#d84f5b] uppercase tracking-wider font-medium">
                    {event.category}
                  </span>
                  <h3 className="font-serif text-xl text-[#3c4a2a] mt-2 mb-2">
                    {event.title}
                  </h3>
                  <p className="text-[#5f6d51]/70 text-sm mb-1">
                    {event.time} • <MapLink location={event.location} testId={`home-event-map-link-${event.id}`} />
                  </p>
                  {(event.rsvp_count || 0) > 0 && (
                    <p className="text-[#a3688f] text-sm font-medium mb-3" data-testid={`home-event-attending-${event.id}`}>
                      {event.rsvp_count} {event.rsvp_count === 1 ? 'woman' : 'women'} attending
                    </p>
                  )}
                  <Link to="/events">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white"
                    >
                      RSVP Now
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section - White
const TestimonialsSection = () => {
  const { c } = useContent();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#5d6b3c] text-xs uppercase tracking-[0.2em] font-medium">
            {c('home.voices.eyebrow', 'Founding Voices')}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-4">
            {c('home.voices.heading', 'Voices of Our Community')}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <Card
              key={testimonial.id}
              className="bg-[#f1e3eb]/30 border-0 p-8"
            >
              <div className="quote-mark leading-none mb-2">"</div>
              <p className="text-[#5f6d51] italic mb-6 leading-relaxed">
                {testimonial.quote}
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-medium text-[#3c4a2a]">{testimonial.name}</p>
                  <p className="text-sm text-[#5f6d51]/60">{testimonial.title}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section - Authority Green
const CTASection = () => {
  const { c } = useContent();
  return (
    <section className="py-24 bg-[#5d6b3c] relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
          {c('home.cta.heading', 'Ready to Make a Difference?')}
        </h2>
        <p className="text-white/85 text-lg mb-10 max-w-2xl mx-auto">
          {c('home.cta.text', 'Join a community of women dedicated to leadership, empowerment, and building a stronger Lincoln County. Your voice matters here.')}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Link to="/membership">
            <Button className="w-full sm:w-auto bg-[#f1e3eb] hover:bg-white text-[#3c4a2a] px-8 py-6 text-lg">
              Become a Member
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <Link to="/donate">
            <Button
              variant="outline"
              className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-[#5d6b3c] px-8 py-6 text-lg"
            >
              Support Our Mission
              <Heart className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Main Home Page Component
const HomePage = () => {
  const [programs, setPrograms] = React.useState(mockPrograms);
  const [events, setEvents] = React.useState(mockEvents);
  React.useEffect(() => {
    api.get('/programs').then((res) => { if (res.data?.length) setPrograms(res.data); }).catch(() => {});
    api.get('/events').then((res) => { if (res.data?.length) setEvents(res.data); }).catch(() => {});
  }, []);

  return (
    <main>
      <KickoffBanner />
      <HeroSection />
      <StatsSection />
      <MissionSection />
      <ProgramsSection programs={programs} />
      <EventsSection events={events} />
      <TestimonialsSection />
      <CTASection />
    </main>
  );
};

export default HomePage;
